import { fabric } from "fabric";
import { arrayToMap } from "../../helpers/mapping";
import { Table } from "../elements/table";
import { defaults } from "../state/defaults";
import {
  FieldViewModes,
  LinkedRecordItem,
  LinkedRecordTableField,
} from "../state/types";
import { normalizeLinkedRecordTableFields } from "../state/utils";
import { extractHttpUrls } from "../../helpers/url";
import { RenderContext } from "./fabric-renderer";
import { Application, BaseRecord } from "../../api/types";
import { RecordTextJson } from "../../api/client";
import { calculateTopWithPageBreak } from "./fabric-renderer-utils";

export type LinkedRecordElement = fabric.Textbox | Table;

type TableField = LinkedRecordTableField & { label: string };

export function renderLinkedRecord(
  context: RenderContext,
  item: LinkedRecordItem,
  element: LinkedRecordElement | undefined,
  canvas: fabric.Canvas,
): LinkedRecordElement {
  element = createElement(item, element, canvas);

  if (shouldRenderElement(context, item)) {
    // Elements are validated in the shouldRenderElement function. We can safely assume that the
    // linked application, the record, and the linked records are available.
    const applications = context.linkedApplications as Record<
      string,
      Application
    >;
    const record = context.record as BaseRecord;
    const linkedApplication = applications[item.field_linked_application];

    let linkedRecords = context.linkedRecords?.[item.id] ?? [];
    const useFieldValueOrder = !item.sort?.length;
    if (useFieldValueOrder) {
      const recordsMapping = arrayToMap(
        context.linkedRecords?.[item.id] ?? [],
        "id",
      );
      linkedRecords = (record[item.field_slug] as string[])
        .map((id) => recordsMapping.get(id))
        .filter((rec) => !!rec) as RecordTextJson[];
    }

    // Set the element options, without setting the HEIGHT. The height is dynamically calculated.
    element.setOptions({
      id: item.id,
      left: item.left,
      top: item.top,
      width: item.width,
      angle: item.angle,
      scaleX: 1,
      scaleY: 1,
    });

    if (item.mode === FieldViewModes.table) {
      return renderTable(
        context,
        element as Table,
        item,
        linkedApplication,
        linkedRecords,
      );
    }

    return renderText(context, element as fabric.Textbox, item, linkedRecords);
  }

  return element;
}

function createElement(
  item: LinkedRecordItem,
  element: LinkedRecordElement | undefined,
  canvas: fabric.Canvas,
): LinkedRecordElement {
  // Remove element from canvas if the view mode has changed
  if (element && isConflictingViewMode(element, item)) {
    canvas.remove(element);
    element = undefined;
  }

  // Create a new element if it does not exist or it was removed from the canvas
  if (!element) {
    element =
      item.mode === FieldViewModes.table
        ? new Table()
        : new fabric.Textbox("", {
            fontSize: defaults.fontSize,
            fontFamily: defaults.fontFamily,
            editable: false,
          });
  }

  return element;
}

function isConflictingViewMode(
  element: LinkedRecordElement,
  item: LinkedRecordItem,
): boolean {
  return element.type === "table"
    ? item.mode !== FieldViewModes.table
    : item.mode === FieldViewModes.table;
}

function shouldRenderElement(
  context: RenderContext,
  item: LinkedRecordItem,
): boolean {
  return Boolean(
    context.linkedApplications?.[item.field_linked_application] &&
      context.record &&
      context.linkedRecords &&
      context.record[item.field_slug],
  );
}

function getTableSelectedFields(
  item: LinkedRecordItem,
  linkedApplication: Application,
): Record<string, TableField> {
  const selectedFields = normalizeLinkedRecordTableFields(item);
  const fieldListSet = new Set(selectedFields.map((field) => field.field));

  // Create a map with selected fields and their labels
  const fieldListWithoutDeletedOnes = linkedApplication.structure.reduce<
    Record<string, string>
  >((acc, field) => {
    if (fieldListSet.has(field.slug)) {
      acc[field.slug] = field.label;
    }

    return acc;
  }, {});

  // Return selected fields sorted by the order in the field list
  return selectedFields.reduce<Record<string, TableField>>((acc, field) => {
    const fieldSlug = field.field;
    if (fieldListWithoutDeletedOnes[fieldSlug]) {
      acc[fieldSlug] = {
        ...field,
        label: fieldListWithoutDeletedOnes[fieldSlug],
      };
    }

    return acc;
  }, {});
}

function renderTable(
  context: RenderContext,
  element: Table,
  item: LinkedRecordItem,
  application: Application,
  records: RecordTextJson[],
): Table {
  // Get the selected fields to display in the table element with their labels
  const fields = getTableSelectedFields(item, application);

  const headers = Object.values(fields).map((field) => field.label);
  const alignments = Object.values(fields).map(
    (field) => field.textAlign ?? "left",
  );
  const columnWidths = Object.values(fields).map((field) => field.width);
  const rows = records.map((record) =>
    Object.entries(fields).map(([slug]) => record?.[slug] ?? ""),
  );

  if (headers.length >= 1) {
    element.height = item.height;
    element.set("columnWidths", columnWidths);
    element.set("pageHeight", context.dimensions.height);
    element.set("pageBreakMargin", context.dimensions.pageBreakMargin ?? 0);
    element.set("fontSize", item.fontSize);
    element.set("fontFamily", item.fontFamily);
    element.set("fontWeight", item.fontWeight);
    element.set("lineHeight", item.lineHeight);
    element.set("textAlign", item.textAlign);
    element.set("textColor", item.fill ?? defaults.textColor);
    element.addCells(headers, rows, alignments);
    markUrlCells(element, rows, headers.length);
  } else {
    element.addCells([], [], []);
    element.set("columnWidths", []);
  }

  element.set("data", {
    strokeStyle: item?.stroke,
    lineWidth: item?.strokeWidth,
  });

  return element;
}

/**
 * Stores the full url(s) on cells whose value contains links, so the pdf
 * export can attach working annotations — the visible cell text may be
 * wrapped or truncated with an ellipsis to fit the column width. Multi-value
 * link fields arrive joined into one string and are kept as a list.
 */
function markUrlCells(
  element: Table,
  rows: string[][],
  columnsCount: number,
): void {
  const rowCells = element.getRowCells();

  rows.forEach((row, rowIndex) =>
    row.forEach((value, columnIndex) => {
      const cell = rowCells[rowIndex * columnsCount + columnIndex];
      const urls = cell ? extractHttpUrls(value) : [];
      if (cell && urls.length) {
        cell.data = {
          ...cell.data,
          url: urls.length === 1 ? urls[0] : undefined,
          urls,
        };
      }
    }),
  );
}

export function renderText(
  context: RenderContext,
  element: fabric.Textbox,
  item: LinkedRecordItem,
  records: RecordTextJson[],
): fabric.Textbox {
  const content =
    item.mode === FieldViewModes.list
      ? records.map((item) => `•  ${item.title}`).join("\n")
      : records.map((item) => item.title).join(", ");

  element.set("text", content);
  element.set("fontSize", item.fontSize);
  element.set("fontFamily", item.fontFamily);
  element.set("fontWeight", item.fontWeight);
  element.set("lineHeight", item.lineHeight);
  element.set("textAlign", item.textAlign);
  element.set("fill", item.fill ?? defaults.textColor);

  // Normalize element position to avoid page break
  const { updated, newTop } = calculateTopWithPageBreak(
    element,
    context.dimensions,
  );
  if (updated) {
    element.set("top", newTop);
  }

  return element;
}
